import { produce } from "immer";

// Backend does not support dashes when validating; strip them
export const toWorkflowFormSubmissionPathSegment = (id: string): string =>
  id.replace(/-/g, "");

const FORM_SUBMISSION_ANSWER_OUTPUT_PATH_REGEX =
  /^\$\.answers\.([^.[]+)\[([^\]]+)\]\.([^.]+)\.value$/;

type FormSubmissionAnswerOutputPathType = {
  sectionId: string;
  sectionIndex: string;
  questionId: string;
};

export const matchFormSubmissionAnswerOutputPath = (
  path: string
): FormSubmissionAnswerOutputPathType | null => {
  const match = path.match(FORM_SUBMISSION_ANSWER_OUTPUT_PATH_REGEX);
  if (!match) return null;

  const [, sectionId, sectionIndex, questionId] = match;
  return { sectionId, sectionIndex, questionId };
};

export const buildFormSubmissionAnswerOutputPath = ({
  sectionId,
  sectionIndex,
  questionId,
}: FormSubmissionAnswerOutputPathType): string =>
  `$.answers.${toWorkflowFormSubmissionPathSegment(sectionId)}[${sectionIndex}].${toWorkflowFormSubmissionPathSegment(questionId)}.value`;

export const normalizeFormSubmissionAnswerOutputPath = (
  path: string
): string => {
  const parsed = matchFormSubmissionAnswerOutputPath(path);
  return parsed ? buildFormSubmissionAnswerOutputPath(parsed) : path;
};

export const formSchemaIdMatchesWorkflowPathSegment = (
  formSchemaId: string,
  pathSegment: string
): boolean =>
  toWorkflowFormSubmissionPathSegment(formSchemaId) ===
  toWorkflowFormSubmissionPathSegment(pathSegment);

export const formSchemaSectionIdMatchesPathSegment = (
  sectionId: string,
  pathSectionId: string
): boolean => formSchemaIdMatchesWorkflowPathSegment(sectionId, pathSectionId);

export const normalizeFormSubmissionAnswerOutputVariables = (
  output: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(output).map(([key, value]) => [
      key,
      normalizeFormSubmissionAnswerOutputPath(value),
    ])
  );

export type OutputVariableFormValuesType = {
  variableSourceType?: "primaryFields" | "formSubmissionAnswers";
  answerSectionId?: string;
  answerSectionIndex?: string;
  answerQuestionId?: string;
  primaryField?: string;
  variableName: string;
};

export const applyOutputVariableFormSubmission = (
  currentVariables: Record<string, string>,
  data: OutputVariableFormValuesType
): Record<string, string> | null => {
  if (!data.variableSourceType || !data.variableName) return null;

  if (data.variableSourceType === "primaryFields" && data.primaryField) {
    return produce(currentVariables, (draft) => {
      draft[data.variableName] = `$.${data.primaryField}`;
    });
  }

  if (
    data.variableSourceType === "formSubmissionAnswers" &&
    data.answerQuestionId &&
    data.answerSectionId &&
    data.answerSectionIndex
  ) {
    return produce(currentVariables, (draft) => {
      draft[data.variableName] = buildFormSubmissionAnswerOutputPath({
        sectionId: data.answerSectionId!,
        sectionIndex: data.answerSectionIndex!,
        questionId: data.answerQuestionId!,
      });
    });
  }

  return null;
};
